import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconCheck,
  IconDots,
  IconUserMinus,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { client } from "@/api-client";
import type { GroupParticipantOutput, MembershipStatus } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Portrait } from "@/components/Portrait";

interface GroupParticipantsProps {
  groupId: string;
}

const ParticipantCard = ({
  participant,
  groupId,
  refetch,
}: {
  participant: GroupParticipantOutput;
  groupId: string;
  refetch: () => void;
}) => {
  const removeParticipant = client.useMutation(
    "delete",
    "/trainer/groups/{groupId}/participants/{ownerId}",
  );

  const handleRemove = () => {
    modals.openConfirmModal({
      title: "Remove Participant",
      children: (
        <Text>
          Are you sure you want to remove{" "}
          <strong>
            {participant.owner.firstName} {participant.owner.lastName}
          </strong>{" "}
          from this group?
        </Text>
      ),
      labels: { confirm: "Remove", cancel: "Cancel" },
      confirmProps: { color: "red", loading: removeParticipant.isPending },
      onConfirm: async () => {
        await removeParticipant.mutateAsync({
          params: {
            path: { groupId: groupId, ownerId: participant.owner.id },
          },
        });
        refetch();
      },
    });
  };

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between">
        <Group>
          <Portrait
            profile={{
              firstName: participant.owner.firstName,
              lastName: participant.owner.lastName,
              portraitFullPath: participant.owner.portraitFullPath,
            }}
            size="40"
            who="person"
          />
          <div>
            <Text fw={500}>
              {participant.owner.firstName} {participant.owner.lastName}
            </Text>
            <Text size="sm" c="dimmed">
              {participant.dog.name} • {participant.dog.breed}
            </Text>
          </div>
        </Group>

        <Group>
          <Badge
            color={
              participant.status === "APPROVED"
                ? "green"
                : participant.status === "REJECTED"
                  ? "red"
                  : "yellow"
            }
            variant="light"
          >
            {participant.status === "APPROVED"
              ? "Active"
              : participant.status === "REJECTED"
                ? "Rejected"
                : "Pending"}
          </Badge>

          {participant.status === "APPROVED" && (
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon variant="subtle">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconUserMinus size={16} />}
                  color="red"
                  onClick={handleRemove}
                >
                  Remove from group
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>

      <Text size="sm" c="dimmed" mt="xs">
        {participant.status === "APPROVED" ? "Joined" : "Requested"}{" "}
        {new Date(participant.joinedAt).toLocaleDateString()}
      </Text>
    </Paper>
  );
};

export const RequestCard = ({
  request,
  groupId,
  refetch,
}: {
  request: GroupParticipantOutput;
  groupId: string;
  refetch: () => void;
}) => {
  const approveRequest = client.useMutation(
    "post",
    "/trainer/groups/{groupId}/join-requests/{ownerId}/approve",
  );
  const rejectRequest = client.useMutation(
    "post",
    "/trainer/groups/{groupId}/join-requests/{ownerId}/reject",
  );
  const [_showNote, _setShowNote] = useState(false);

  const handleApprove = async () => {
    await approveRequest.mutateAsync({
      params: {
        path: { groupId: groupId, ownerId: request.owner.id },
      },
    });
    refetch();
  };

  const handleReject = () => {
    modals.openConfirmModal({
      title: "Reject Request",
      children: (
        <Text>
          Are you sure you want to reject{" "}
          <strong>
            {request.owner.firstName} {request.owner.lastName}
          </strong>
          's request to join this group?
        </Text>
      ),
      labels: { confirm: "Reject", cancel: "Cancel" },
      confirmProps: { color: "red", loading: rejectRequest.isPending },
      onConfirm: async () => {
        await rejectRequest.mutateAsync({
          params: {
            path: { groupId: groupId, ownerId: request.owner.id },
          },
        });
        refetch();
      },
    });
  };

  return (
    <Paper p="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <Group>
            <Portrait
              profile={{
                firstName: request.owner.firstName,
                lastName: request.owner.lastName,
                portraitFullPath: request.owner.portraitFullPath,
              }}
              size="40"
              who="person"
            />
            <div>
              <Text fw={500}>
                {request.owner.firstName} {request.owner.lastName}
              </Text>
              <Text size="sm" c="dimmed">
                {request.dog.name} • {request.dog.breed}
              </Text>
            </div>
          </Group>

          <Group>
            <Button
              size="sm"
              variant="outline"
              color="red"
              leftSection={<IconX size={16} />}
              onClick={handleReject}
              loading={rejectRequest.isPending}
            >
              Reject
            </Button>
            <Button
              size="sm"
              leftSection={<IconCheck size={16} />}
              onClick={handleApprove}
              loading={approveRequest.isPending}
            >
              Approve
            </Button>
          </Group>
        </Group>

        <Text size="sm" c="dimmed">
          Requested {new Date(request.joinedAt).toLocaleDateString()}
        </Text>

        {request.note && (
          <Paper p="sm" bg="gray.0" mt="xs">
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {request.note}
            </Text>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
};

export const GroupParticipants = ({ groupId }: GroupParticipantsProps) => {
  const {
    data: group,
    isLoading,
    refetch,
  } = client.useQuery("get", "/trainer/groups/{groupId}", {
    params: { path: { groupId: groupId } },
  });
  const [filter, setFilter] = useState<MembershipStatus>("APPROVED");

  if (isLoading || !group) {
    return <FancyLoader />;
  }

  const filteredParticipants = group.participants.filter(
    (participant) => participant.status === filter,
  );

  const grantedCount = group.participants.filter(
    (p) => p.status === "APPROVED",
  ).length;
  const requestedCount = group.participants.filter(
    (p) => p.status === "REQUESTED",
  ).length;
  const rejectedCount = group.participants.filter(
    (p) => p.status === "REJECTED",
  ).length;

  return (
    <Stack>
      <Tabs
        value={filter}
        onChange={(value) => value && setFilter(value as MembershipStatus)}
      >
        <Tabs.List grow>
          <Tabs.Tab value="APPROVED">Active ({grantedCount})</Tabs.Tab>
          <Tabs.Tab value="REQUESTED">Requests ({requestedCount})</Tabs.Tab>
          <Tabs.Tab value="REJECTED">Rejected ({rejectedCount})</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {filteredParticipants.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center">
            <IconUsers size={48} color="gray" />
            <div>
              <Title order={4} ta="center">
                No participants
              </Title>
              <Text c="dimmed" ta="center">
                {filter === "APPROVED"
                  ? "No active participants yet"
                  : filter === "REQUESTED"
                    ? "No pending requests"
                    : "No rejected participants"}
              </Text>
            </div>
          </Stack>
        </Paper>
      ) : (
        <Stack>
          {filteredParticipants.map((participant) =>
            filter === "REQUESTED" ? (
              <RequestCard
                key={participant.id}
                request={participant}
                groupId={groupId}
                refetch={refetch}
              />
            ) : (
              <ParticipantCard
                key={participant.id}
                participant={participant}
                groupId={groupId}
                refetch={refetch}
              />
            ),
          )}
        </Stack>
      )}
    </Stack>
  );
};
